import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { cn } from '../../../../../lib/utils';
import { ThumbsUp, ArrowRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { WeldPassList } from './WeldPassList';

interface WeldPass {
  name: string;
  status: 'Completed' | 'Not Started' | 'In Progress' | 'Rejected';
  welderId: string;
  duration: string;
  startTime?: string;
  endTime?: string;
  voltage?: { min: number; avg: number; max: number };
  current?: { min: number; avg: number; max: number };
  travelSpeed?: { min: number; avg: number; max: number };
  heat?: { min: number; avg: number; max: number };
  trueEnergy?: { min: number; avg: number; max: number };
  oscillationWidth?: { min: number; avg: number; max: number };
  wireSpeed?: { min: number; avg: number; max: number };
}

export interface WeldData {
  weldNumber: 'Weld 1' | 'Weld 2';
  weldIdentifier: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Rejected';
  weldTime: string;
  passes?: WeldPass[];
}

// Example weld data with realistic values
const exampleWelds: WeldData[] = [
  {
    weldNumber: 'Weld 1',
    weldIdentifier: 'WLD-2024-001',
    status: 'Completed',
    weldTime: '45 mins',
    passes: [
      {
        name: 'Root Pass',
        status: 'Completed',
        welderId: 'WLD-001',
        duration: '15 mins',
        startTime: '2024-03-15 09:00',
        endTime: '2024-03-15 09:15',
        voltage: { min: 21.5, avg: 22.8, max: 24.2 },
        current: { min: 180, avg: 195, max: 210 },
        travelSpeed: { min: 150, avg: 165, max: 180 },
        heat: { min: 1.2, avg: 1.35, max: 1.5 },
        trueEnergy: { min: 2.8, avg: 3.1, max: 3.4 },
        oscillationWidth: { min: 2.5, avg: 2.8, max: 3.1 },
        wireSpeed: { min: 150, avg: 165, max: 180 }
      },
      {
        name: 'Hot Pass',
        status: 'Completed',
        welderId: 'WLD-001',
        duration: '12 mins',
        startTime: '2024-03-15 09:30',
        endTime: '2024-03-15 09:42',
        voltage: { min: 23.5, avg: 24.8, max: 26.2 },
        current: { min: 200, avg: 215, max: 230 },
        travelSpeed: { min: 170, avg: 185, max: 200 },
        heat: { min: 1.4, avg: 1.55, max: 1.7 },
        trueEnergy: { min: 3.2, avg: 3.5, max: 3.8 },
        oscillationWidth: { min: 2.8, avg: 3.1, max: 3.4 },
        wireSpeed: { min: 170, avg: 185, max: 200 }
      },
      {
        name: 'Fill Pass',
        status: 'Completed',
        welderId: 'WLD-002',
        duration: '10 mins',
        startTime: '2024-03-15 10:00',
        endTime: '2024-03-15 10:10',
        voltage: { min: 25.5, avg: 26.8, max: 28.2 },
        current: { min: 220, avg: 235, max: 250 },
        travelSpeed: { min: 190, avg: 205, max: 220 },
        heat: { min: 1.6, avg: 1.75, max: 1.9 },
        trueEnergy: { min: 3.6, avg: 3.9, max: 4.2 },
        oscillationWidth: { min: 3.1, avg: 3.4, max: 3.7 },
        wireSpeed: { min: 190, avg: 205, max: 220 }
      },
      {
        name: 'Cap Pass',
        status: 'Completed',
        welderId: 'WLD-002',
        duration: '8 mins',
        startTime: '2024-03-15 10:30',
        endTime: '2024-03-15 10:38',
        voltage: { min: 27.5, avg: 28.8, max: 30.2 },
        current: { min: 240, avg: 255, max: 270 },
        travelSpeed: { min: 210, avg: 225, max: 240 },
        heat: { min: 1.8, avg: 1.95, max: 2.1 },
        trueEnergy: { min: 4.0, avg: 4.3, max: 4.6 },
        oscillationWidth: { min: 3.4, avg: 3.7, max: 4.0 },
        wireSpeed: { min: 210, avg: 225, max: 240 }
      }
    ]
  },
  {
    weldNumber: 'Weld 2',
    weldIdentifier: 'WLD-2024-002',
    status: 'In Progress',
    weldTime: '35 mins',
    passes: [
      {
        name: 'Root Pass',
        status: 'Completed',
        welderId: 'WLD-001',
        duration: '14 mins',
        startTime: '2024-03-15 11:00',
        endTime: '2024-03-15 11:14',
        voltage: { min: 21.2, avg: 22.5, max: 23.8 },
        current: { min: 175, avg: 190, max: 205 },
        travelSpeed: { min: 145, avg: 160, max: 175 },
        heat: { min: 1.15, avg: 1.3, max: 1.45 },
        trueEnergy: { min: 2.7, avg: 3.0, max: 3.3 },
        oscillationWidth: { min: 2.4, avg: 2.7, max: 3.0 },
        wireSpeed: { min: 145, avg: 160, max: 175 }
      },
      {
        name: 'Hot Pass',
        status: 'Completed',
        welderId: 'WLD-001',
        duration: '11 mins',
        startTime: '2024-03-15 11:30',
        endTime: '2024-03-15 11:41',
        voltage: { min: 23.2, avg: 24.5, max: 25.8 },
        current: { min: 195, avg: 210, max: 225 },
        travelSpeed: { min: 165, avg: 180, max: 195 },
        heat: { min: 1.35, avg: 1.5, max: 1.65 },
        trueEnergy: { min: 3.1, avg: 3.4, max: 3.7 },
        oscillationWidth: { min: 2.7, avg: 3.0, max: 3.3 },
        wireSpeed: { min: 165, avg: 180, max: 195 }
      },
      {
        name: 'Fill Pass',
        status: 'In Progress',
        welderId: 'WLD-002',
        duration: '10 mins',
        startTime: '2024-03-15 12:00',
        endTime: '-',
        voltage: { min: 25.2, avg: 26.5, max: 27.8 },
        current: { min: 215, avg: 230, max: 245 },
        travelSpeed: { min: 185, avg: 200, max: 215 },
        heat: { min: 1.55, avg: 1.7, max: 1.85 },
        trueEnergy: { min: 3.5, avg: 3.8, max: 4.1 },
        oscillationWidth: { min: 3.0, avg: 3.3, max: 3.6 },
        wireSpeed: { min: 185, avg: 200, max: 215 }
      },
      {
        name: 'Cap Pass',
        status: 'Not Started',
        welderId: '-',
        duration: '-',
        startTime: '-',
        endTime: '-',
        voltage: undefined,
        current: undefined,
        travelSpeed: undefined,
        heat: undefined,
        trueEnergy: undefined,
        oscillationWidth: undefined,
        wireSpeed: undefined
      }
    ]
  }
];

interface WeldDataTableProps {
  assemblyNumber: string;
  welds: WeldData[];
}

export const WeldDataTable: React.FC<WeldDataTableProps> = ({
  assemblyNumber,
  welds = exampleWelds // Use example data as default
}) => {
  const [selectedWeld, setSelectedWeld] = useState<WeldData | null>(null);

  const handleOpenWeldPasses = (weld: WeldData) => {
    setSelectedWeld(weld);
  };

  const handleCloseWeldPasses = () => {
    setSelectedWeld(null);
  };

  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-center">Weld Number</TableHead>
            <TableHead className="text-center">Weld Identifier</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-center">Weld Duration</TableHead>
            <TableHead className="text-center w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {welds.map((weld) => (
            <TableRow key={weld.weldNumber}>
              <TableCell className="text-center font-medium">{weld.weldNumber}</TableCell>
              <TableCell className="text-center">{weld.weldIdentifier}</TableCell>
              <TableCell className="text-center">
                <div className="inline-flex justify-center">
                  <ThumbsUp className="h-5 w-5 text-green-600" />
                </div>
              </TableCell>
              <TableCell className="text-center">{weld.weldTime}</TableCell>
              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="hover:bg-gray-200"
                  onClick={() => handleOpenWeldPasses(weld)}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selectedWeld && (
        <WeldPassList
          isOpen={true}
          onClose={handleCloseWeldPasses}
          weld={selectedWeld}
          assemblyNumber={assemblyNumber}
        />
      )}
    </div>
  );
}; 